import { useState, useEffect } from "react";
import { useSearchParams, Link, useNavigate, useParams } from "react-router-dom";
import { Preferences } from '@capacitor/preferences';
import { ArrowLeft, Check, Clock, User, Phone, Users, Store, Scissors, MapPin, CheckCircle, Loader2, LogOut, Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import axios from "axios";
import { format, addDays, isBefore, startOfDay } from "date-fns";
import { es } from "date-fns/locale";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingPage = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [shopInfo, setShopInfo] = useState(null);
  const [loadingShop, setLoadingShop] = useState(!!slug);

  const [formData, setFormData] = useState({
    service: searchParams.get("service") || "",
    date: null,
    time: "",
    client_name: "",
    client_phone: ""
  });

  const [bookingResult, setBookingResult] = useState(null);

  const handleChangeShop = async () => {
    if (window.confirm("¿Deseas buscar otra barbería?")) {
      await Preferences.remove({ key: 'favorite_barber_slug' });
      navigate("/", { replace: true });
    }
  };

  useEffect(() => {
    if (slug) {
      const fetchShop = async () => {
        setLoadingShop(true);
        try {
          const response = await axios.get(`${API}/shop/${slug}`);
          setShopInfo(response.data);
          if (response.data.services && response.data.services.length > 0) {
            setServices(response.data.services);
          }
        } catch (error) {
          toast.error("Barbería no encontrada");
          navigate("/");
        } finally {
          setLoadingShop(false);
        }
      };
      fetchShop();
    }
  }, [slug, navigate]);

  useEffect(() => {
    if (!slug) {
      const fetchServices = async () => {
        try {
          const response = await axios.get(`${API}/services`);
          setServices(response.data);
        } catch (error) {
          toast.error("Error al cargar servicios");
        }
      };
      fetchServices();
    }
  }, [slug]);

  useEffect(() => {
    if (formData.date) {
      const fetchSlots = async () => {
        try {
          const dateStr = format(formData.date, "yyyy-MM-dd");
          let url = `${API}/available-slots/${dateStr}`;
          if (slug) {
            url += `?slug=${slug}`;
          } else if (shopInfo?.id) {
            url += `?admin_id=${shopInfo.id}`;
          }
          const response = await axios.get(url);
          setTimeSlots(response.data.slots || []);
        } catch (error) {
          toast.error("Error al cargar horarios");
        }
      };
      fetchSlots();
    }
  }, [formData.date, slug, shopInfo]);

  const selectedService = services.find(s => s.id === formData.service);

  const getPeopleAhead = () => {
    if (!formData.time || !timeSlots.length) return 0;
    const selectedIndex = timeSlots.findIndex(s => s.time === formData.time);
    return timeSlots.slice(0, selectedIndex).filter(s => !s.available).length;
  };

  const handleServiceSelect = (serviceId) => {
    setFormData({ ...formData, service: serviceId });
    setStep(2);
  };

  const handleDateSelect = (date) => {
    setFormData({ ...formData, date, time: "" });
  };

  const handleTimeSelect = (time) => {
    setFormData({ ...formData, time });
    setStep(3);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.client_name || !formData.client_phone) {
      toast.error("Por favor completa todos los campos");
      return;
    }

    setLoading(true);
    try {
      // 🔔 Leer el token FCM guardado en el dispositivo
      const { value: fcmToken } = await Preferences.get({ key: 'fcm_token' });

      const appointmentData = {
        client_name: formData.client_name,
        client_phone: formData.client_phone,
        service: formData.service,
        date: format(formData.date, "yyyy-MM-dd"),
        time: formData.time,
        fcmToken: fcmToken || null  // 🔔 Se envía al backend para guardar en la cita
      };

      if (slug) {
        appointmentData.slug = slug;
      } else if (shopInfo?.id) {
        appointmentData.admin_id = shopInfo.id;
      }

      const response = await axios.post(`${API}/public/appointments`, appointmentData);
      toast.success("¡Cita reservada exitosamente!");
      setBookingResult(response.data);
      setStep(4);
    } catch (error) {
      const message = error.response?.data?.detail || "Error al crear la cita";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const disabledDays = (date) => {
    const today = startOfDay(new Date());
    const day = date.getDay();
    return isBefore(date, today) || day === 1;
  };

  if (loadingShop) {
    return (
      <div className="min-h-screen bg-[#09090b] flex flex-col items-center justify-center">
        <Loader2 className="w-10 h-10 text-amber-500 animate-spin mb-4" />
        <p className="text-zinc-500 font-heading uppercase tracking-widest text-sm">Cargando Barbería...</p>
      </div>
    );
  }

  const shopName = shopInfo?.shop_name || "Barbershop";
  const homeLink = slug ? `/reservar/${slug}` : "/";
  const myAppointmentLink = slug ? `/mi-cita/${slug}` : "/mi-cita";

  return (
    <div className="min-h-screen bg-[#09090b] pb-24">
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-black/80 border-b border-zinc-800 pt-[max(1rem,env(safe-area-inset-top))] pb-4 px-6 shadow-xl">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Link to={homeLink} className="flex items-center gap-3">
            <img
              src={shopInfo?.logo_url || "/logo192.png"}
              alt="Logo"
              className="w-10 h-10 rounded-xl object-cover border border-amber-500/30 bg-zinc-900"
              onError={(e) => { e.target.onerror = null; e.target.src = "/logo192.png"; }}
            />
            <span className="font-heading text-xl font-black uppercase tracking-wider text-white truncate max-w-[150px] sm:max-w-none">
              {shopName}
            </span>
          </Link>

          <div className="flex items-center gap-2">
            {step === 1 && (
              <button
                onClick={handleChangeShop}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors text-xs font-bold uppercase tracking-wider"
              >
                <LogOut className="w-3 h-3" /> Cambiar
              </button>
            )}
            {step > 1 && step < 4 && (
              <button
                onClick={() => setStep(step - 1)}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </nav>

      <main className="max-w-xl mx-auto px-6 py-8">

        {step < 4 && (
          <div className="flex items-center justify-center mb-10">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs transition-colors duration-300 ${
                  step >= s ? "bg-amber-500 text-black shadow-[0_0_10px_rgba(245,158,11,0.5)]" : "bg-zinc-900 border border-zinc-800 text-zinc-500"
                }`}>
                  {step > s ? <Check className="w-4 h-4" /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-12 h-[2px] mx-2 transition-colors duration-300 ${step > s ? "bg-amber-500" : "bg-zinc-800"}`} />
                )}
              </div>
            ))}
          </div>
        )}

        {/* STEP 1: Servicios */}
        {step === 1 && (
          <div className="animate-fade-in">
            <div className="mb-8">
              <h2 className="font-heading text-2xl font-black uppercase text-white mb-2">Elige tu Servicio</h2>
              <p className="text-zinc-500 text-sm">Selecciona lo que te vas a hacer hoy.</p>
            </div>

            <div className="grid gap-3 mb-8">
              {services.length > 0 ? services.map((service) => (
                <button
                  key={service.id}
                  onClick={() => handleServiceSelect(service.id)}
                  className={`flex items-center justify-between p-5 rounded-2xl border transition-all text-left ${
                    formData.service === service.id
                      ? "bg-amber-500/10 border-amber-500 shadow-[0_0_15px_rgba(245,158,11,0.1)] scale-[1.02]"
                      : "bg-zinc-900 border-zinc-800 hover:border-zinc-700"
                  }`}
                >
                  <div>
                    <h3 className={`font-bold text-lg ${formData.service === service.id ? 'text-amber-500' : 'text-white'}`}>
                      {service.name}
                    </h3>
                    <span className="flex items-center text-zinc-500 text-xs mt-1 font-medium">
                      <Clock className="w-3 h-3 mr-1" /> {service.duration} min
                    </span>
                  </div>
                  <div className="text-right">
                    <span className={`font-heading font-black text-2xl ${formData.service === service.id ? 'text-amber-500' : 'text-zinc-300'}`}>
                      {service.price} <span className="text-sm font-normal text-zinc-500">DOP</span>
                    </span>
                  </div>
                </button>
              )) : (
                <div className="text-center py-12 bg-zinc-900 rounded-2xl border border-zinc-800">
                  <Scissors className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500">No hay servicios configurados aún.</p>
                </div>
              )}
            </div>

            <div className="border-t border-zinc-800 pt-8">
              <p className="text-center text-zinc-500 text-xs font-bold uppercase tracking-widest mb-4">¿Ya tienes una reserva?</p>
              <Link to={myAppointmentLink} className="block w-full">
                <button className="w-full flex items-center justify-center gap-2 bg-zinc-900 border border-zinc-700 text-white font-heading font-bold uppercase tracking-wider h-14 rounded-xl hover:bg-zinc-800 hover:border-amber-500/50 transition-all shadow-lg">
                  <CalendarIcon className="w-5 h-5 text-amber-500" /> Consultar mis citas
                </button>
              </Link>
            </div>
          </div>
        )}

        {/* STEP 2: Fecha y Hora */}
        {step === 2 && (
          <div className="animate-fade-in">
            <div className="mb-8">
              <h2 className="font-heading text-2xl font-black uppercase text-white mb-2">Fecha y Hora</h2>
              <p className="text-zinc-500 text-sm flex items-center gap-2">
                <Scissors className="w-4 h-4 text-amber-500" /> {selectedService?.name}
              </p>
            </div>

            <div className="space-y-6">
              <div className="bg-zinc-900 border border-zinc-800 p-4 rounded-2xl flex justify-center">
                <Calendar
                  mode="single"
                  selected={formData.date}
                  onSelect={handleDateSelect}
                  disabled={disabledDays}
                  locale={es}
                  fromDate={new Date()}
                  toDate={addDays(new Date(), 30)}
                  className="text-white mx-auto"
                />
              </div>
              <p className="text-center text-zinc-600 text-xs uppercase tracking-wider font-bold">
                Cerrado los lunes
              </p>

              {formData.date && (
                <div className="bg-zinc-900 border border-zinc-800 p-5 rounded-2xl mt-6">
                  <h3 className="font-heading text-sm font-bold uppercase text-zinc-400 mb-4 tracking-wider">
                    Horarios Disponibles
                  </h3>
                  {timeSlots.length === 0 ? (
                    <p className="text-red-400 text-sm text-center py-6 bg-red-500/10 rounded-xl">
                      Día completamente lleno o cerrado.
                    </p>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                      {timeSlots.map((slot) => (
                        <button
                          key={slot.time}
                          onClick={() => slot.available && handleTimeSelect(slot.time)}
                          disabled={!slot.available}
                          className={`py-3 rounded-xl text-sm font-bold transition-all ${
                            !slot.available
                              ? "bg-black text-zinc-700 opacity-50 cursor-not-allowed border border-zinc-900"
                              : formData.time === slot.time
                                ? "bg-amber-500 text-black shadow-lg scale-105"
                                : "bg-black text-white border border-zinc-800 hover:border-amber-500/50"
                          }`}
                        >
                          {slot.time}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* STEP 3: Datos del Cliente */}
        {step === 3 && (
          <div className="animate-fade-in">
            <div className="mb-8">
              <h2 className="font-heading text-2xl font-black uppercase text-white mb-2">Tus Datos</h2>
              <p className="text-zinc-500 text-sm">Casi terminamos. Ingresa tu contacto.</p>
            </div>

            <div className="bg-zinc-900 border border-amber-500/20 rounded-2xl p-6 mb-8 shadow-xl">
              <div className="space-y-4">
                <div className="flex justify-between items-center border-b border-zinc-800 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
                      <Scissors className="w-5 h-5 text-amber-500" />
                    </div>
                    <div>
                      <p className="text-zinc-400 text-xs uppercase font-bold tracking-wider">Servicio</p>
                      <p className="text-white font-bold">{selectedService?.name}</p>
                    </div>
                  </div>
                  <span className="font-heading text-xl font-black text-amber-500">{selectedService?.price} DOP</span>
                </div>
                <div className="flex justify-between items-center pt-2">
                  <div>
                    <p className="text-zinc-400 text-xs uppercase font-bold tracking-wider mb-1">Cuándo</p>
                    <p className="text-white font-medium capitalize">
                      {formData.date && format(formData.date, "EEEE d MMM", { locale: es })} a las <strong className="text-amber-500">{formData.time}</strong>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-zinc-400 text-xs uppercase font-bold tracking-wider mb-1">Cola</p>
                    <p className="text-white font-medium flex items-center gap-1 justify-end">
                      <Users className="w-4 h-4 text-amber-500" /> {getPeopleAhead()} pers.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider font-bold mb-2 block ml-1">
                  Nombre Completo
                </Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <Input
                    name="client_name"
                    value={formData.client_name}
                    onChange={handleInputChange}
                    placeholder="Ej: Carlos Gómez"
                    className="w-full bg-zinc-900 border-zinc-800 text-white rounded-xl pl-12 h-14 focus:border-amber-500 transition-colors"
                    required
                  />
                </div>
              </div>

              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider font-bold mb-2 block ml-1">
                  Teléfono (WhatsApp)
                </Label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <Input
                    name="client_phone"
                    type="tel"
                    value={formData.client_phone}
                    onChange={handleInputChange}
                    placeholder="809-000-0000"
                    className="w-full bg-zinc-900 border-zinc-800 text-white rounded-xl pl-12 h-14 focus:border-amber-500 transition-colors"
                    required
                  />
                </div>
                <p className="text-amber-500/80 text-xs mt-2 ml-1 font-medium">
                  Te enviaremos tu recordatorio a este número.
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-amber-500 text-black font-heading font-black uppercase tracking-widest h-14 rounded-xl mt-4 shadow-[0_0_20px_rgba(245,158,11,0.2)] hover:bg-amber-400 hover:scale-[1.02] transition-all disabled:opacity-50 disabled:scale-100 disabled:shadow-none flex items-center justify-center"
              >
                {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : "Confirmar Reserva"}
              </button>
            </form>
          </div>
        )}

        {/* STEP 4: Confirmación */}
        {step === 4 && (
          <div className="animate-fade-in text-center flex flex-col items-center justify-center py-8">
            <div className="w-24 h-24 bg-green-500/10 rounded-full flex items-center justify-center mb-6 border border-green-500/20">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>

            <h2 className="font-heading text-3xl font-black uppercase text-white mb-2">
              ¡Cita Confirmada!
            </h2>
            <p className="text-zinc-400 mb-8 max-w-sm mx-auto">
              Te hemos guardado un espacio. Acércate a la barbería unos minutos antes.
            </p>

            <div className="bg-zinc-900 border border-zinc-800 p-6 rounded-2xl w-full text-left mb-8">
              <div className="flex items-center gap-3 border-b border-zinc-800 pb-4 mb-4">
                <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-500" />
                </div>
                <div>
                  <p className="text-white font-bold capitalize">
                    {formData.date && format(formData.date, "EEEE d 'de' MMMM", { locale: es })}
                  </p>
                  <p className="text-amber-500 font-black">{formData.time}</p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-zinc-300">
                  <User className="w-4 h-4 text-zinc-500" /> <span>{formData.client_name}</span>
                </div>
                <div className="flex items-center gap-3 text-zinc-300">
                  <Scissors className="w-4 h-4 text-zinc-500" /> <span>{selectedService?.name}</span>
                </div>
                {shopInfo?.address && (
                  <div className="flex items-center gap-3 text-zinc-300">
                    <MapPin className="w-4 h-4 text-zinc-500" /> <span>{shopInfo.address}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col w-full gap-3">
              <Link to={`${myAppointmentLink}?phone=${formData.client_phone}`} className="w-full">
                <button className="w-full bg-amber-500 text-black font-bold uppercase tracking-wider h-14 rounded-xl hover:bg-amber-400 transition-colors">
                  Ver Estado de mi Cita
                </button>
              </Link>
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-zinc-900 border border-zinc-800 text-white font-bold uppercase tracking-wider h-14 rounded-xl hover:bg-zinc-800 transition-colors text-sm"
              >
                Hacer Otra Reserva
              </button>
            </div>
          </div>
        )}

      </main>
    </div>
  );
};

export default BookingPage;